import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LeadFormSection } from "@/components/LeadFormSection";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { GoogleReviewBadge } from "@/components/GoogleReviewBadge";
import { TestimonialCards } from "@/components/TestimonialCards";
import { PricingSection } from "@/components/PricingSection";
import { CheckCircle, Target, Smartphone, Search, TrendingUp, Shield, Clock } from "lucide-react";
import { Helmet } from "react-helmet-async";

const Websites = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/websites/#faq",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you offer a free website build?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The website build is included as part of the monthly plan. We handle the design, setup, and launch so you can focus on your business."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to launch?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Typically a few days to a couple weeks depending on how quickly we get your info, photos, and approvals."
        }
      },
      {
        "@type": "Question",
        "name": "Can I upgrade later?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, you can upgrade from Core to Local SEO Expansion at any time."
        }
      },
      {
        "@type": "Question",
        "name": "Can I cancel anytime?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. It's month-to-month. We earn your business by keeping your website valuable over time."
        }
      },
      {
        "@type": "Question",
        "name": "Do I own the website?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, absolutely. You own 100% of the website, content, and design."
        }
      },
      {
        "@type": "Question",
        "name": "What if I don't have photos or content ready?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No problem. We can source professional stock images and help you write compelling copy. However, your own photos and testimonials will convert better."
        }
      }
    ]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/websites/#webpage",
    "url": "https://www.leafrushmarketing.com/websites/",
    "name": "Website Subscription Pricing for Lawn & Landscaping",
    "description": "Professional lawn care & landscaping website design that gets you more clients. $200-$300/month, $0 upfront. Done-for-you sites that convert.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/websites/#service",
    "name": "Website Subscription for Lawn Care & Landscaping Companies",
    "description": "Professional website design subscription service for lawn care and landscaping businesses. $200-$300/month with $0 upfront.",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "serviceType": "Website Design Subscription"
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "sameAs": []
  };

  return (
    <>
      <SEO 
        title="Lawn Care Website Design Pricing | Leaf Rush"
        description="Lawn care and landscaping website design starting at $200/month. Local SEO, Google visibility, and lead generation included. $0 upfront."
        keywords="lawn care website pricing, landscaping website cost, lawn care website subscription, landscaping website subscription, lawn care marketing"
        canonical="https://www.leafrushmarketing.com/websites/"
        ogUrl="https://www.leafrushmarketing.com/websites/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <div className="flex flex-col">
        {/* 1. HERO */}
        <section className="py-20 md:py-32">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Digital Growth Plans
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                Lawn care website design pricing for{" "}
                <span className="text-primary">landscaping companies</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Website design, SEO, and Google visibility for lawn care and landscaping businesses. $0 upfront. You only pay after you approve and your site goes live.
              </p>
              <div className="flex justify-center pt-2">
                <GoogleReviewBadge rating={5.0} />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto">Get Started</Button>
                </a>
                <a href="#whats-included">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">See What's Included</Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 2. WHY NICHE-ONLY MATTERS */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Why niche-only lawn care website design matters</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Generic agencies don't know the industry. Niche structure converts better.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card>
                <CardHeader>
                  <Target className="h-10 w-10 text-primary mb-4" />
                  <CardTitle>We Know Your Customers</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Homeowners want reliability, fast quotes, and proof of quality. We structure every page to address these concerns.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <TrendingUp className="h-10 w-10 text-primary mb-4" />
                  <CardTitle>We Know Your Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Lawn maintenance, aeration, landscaping design, hardscaping. We position each service for maximum conversion.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <Shield className="h-10 w-10 text-primary mb-4" />
                  <CardTitle>We Know Your Pain Points</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Seasonality, competition, trust issues, lead quality. We've solved these problems with proven website strategies.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* 3. WHAT'S INCLUDED */}
        <section id="whats-included" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What's included in every lawn care website</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A complete digital growth system built specifically for lawn care and landscaping businesses.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="flex gap-4 p-4 rounded-lg border bg-card">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">7-14 Page Custom Website</p>
                  <p className="text-sm text-muted-foreground">Home, About, Services, individual service pages, FAQ, Reviews, Contact, and more.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 rounded-lg border bg-card">
                <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Conversion-Optimized Layout</p>
                  <p className="text-sm text-muted-foreground">Every element designed to turn visitors into leads and booked jobs.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 rounded-lg border bg-card">
                <Search className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Local SEO Structure</p>
                  <p className="text-sm text-muted-foreground">Built to rank for "[service] + [city]" searches in your area.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 rounded-lg border bg-card">
                <Smartphone className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Mobile-First + Fast</p>
                  <p className="text-sm text-muted-foreground">60%+ of searches happen on phones. Your site will load fast and look great.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 rounded-lg border bg-card">
                <TrendingUp className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Tracking-Ready</p>
                  <p className="text-sm text-muted-foreground">Easy integration with call tracking so you know which pages drive calls.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 rounded-lg border bg-card">
                <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Hosting + Maintenance + Support</p>
                  <p className="text-sm text-muted-foreground">Ongoing updates, security, and edits as your business grows.</p>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-muted-foreground">
                Looking for details on a specific service? Check out our{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                  lawn care website design
                </NavLink>{" "}
                or{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                  landscaping website design
                </NavLink>{" "}
                pages.
              </p>
            </div>
          </div>
        </section>

        {/* 4. FAST DELIVERY */}
        <section className="py-16 md:py-20 bg-primary/5">
          <div className="container px-4 md:px-6">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Clock className="h-8 w-8 text-primary" />
                <h2 className="text-3xl md:text-4xl font-bold">Live in 14 Days</h2>
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Fast, professional launch without rushing quality. If you're responsive with content and feedback, we can go live in about two weeks.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Typical timeline: 10-14 days. Rush options available if needed.
              </p>
            </div>

            {/* 3-Column Block */}
            <div className="max-w-5xl mx-auto mb-12">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Column 1: What you get */}
                <div className="p-6 rounded-lg border bg-card">
                  <h3 className="font-bold text-lg mb-4">What you get</h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Conversion-optimized website built for lawn care + landscaping</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Mobile-first layout + fast load speeds</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Local SEO structure (service pages + clear CTAs)</span>
                    </li>
                  </ul>
                </div>

                {/* Column 2: What we need from you */}
                <div className="p-6 rounded-lg border bg-card">
                  <h3 className="font-bold text-lg mb-4">What we need from you</h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Services + service areas</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>A few photos (phone photos are fine)</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Logo + contact info (we can help clean up a logo)</span>
                    </li>
                  </ul>
                </div>

                {/* Column 3: After launch */}
                <div className="p-6 rounded-lg border bg-card">
                  <h3 className="font-bold text-lg mb-4">After launch</h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>We handle updates + fixes</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Request edits anytime</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>Month-to-month</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Timeline Cards - 5 Steps */}
            <div className="max-w-5xl mx-auto mb-12">
              <div className="grid md:grid-cols-5 gap-4">
                <div className="p-5 rounded-lg border bg-card text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mx-auto mb-3">1</div>
                  <p className="font-semibold mb-1">Days 1–2</p>
                  <p className="text-sm font-medium text-foreground mb-2">Discovery & Content</p>
                  <p className="text-xs text-muted-foreground">Confirm services, service areas, goals, and collect basics (logo, photos, contact info).</p>
                </div>
                <div className="p-5 rounded-lg border bg-card text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mx-auto mb-3">2</div>
                  <p className="font-semibold mb-1">Days 3–5</p>
                  <p className="text-sm font-medium text-foreground mb-2">Site Structure + Copy</p>
                  <p className="text-xs text-muted-foreground">We build the page structure and write niche-specific copy designed to drive calls and form fills.</p>
                </div>
                <div className="p-5 rounded-lg border bg-card text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mx-auto mb-3">3</div>
                  <p className="font-semibold mb-1">Days 6–9</p>
                  <p className="text-sm font-medium text-foreground mb-2">Design + Build</p>
                  <p className="text-xs text-muted-foreground">We design and build the full site, optimize mobile layout, and add conversion elements.</p>
                </div>
                <div className="p-5 rounded-lg border bg-card text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mx-auto mb-3">4</div>
                  <p className="font-semibold mb-1">Days 10–12</p>
                  <p className="text-sm font-medium text-foreground mb-2">Review & Revisions</p>
                  <p className="text-xs text-muted-foreground">You review everything once. We apply revisions fast and polish final details.</p>
                </div>
                <div className="p-5 rounded-lg border bg-card text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mx-auto mb-3">5</div>
                  <p className="font-semibold mb-1">Days 13–14</p>
                  <p className="text-sm font-medium text-foreground mb-2">Launch + QA</p>
                  <p className="text-xs text-muted-foreground">We publish, verify forms, tracking, SEO basics, and make sure everything is ready to convert.</p>
                </div>
              </div>
            </div>

            {/* CTA Row */}
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-lg text-muted-foreground mb-6">
                Want to see if you're a fit for a 14-day launch?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto">Get Started</Button>
                </a>
                <a href="#whats-included">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">See What's Included</Button>
                </a>
              </div>
              <a 
                href="https://share.google/BnYOuRH1kPhNa4BeV" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                5-Star Rated - see reviews on Google
              </a>
            </div>
          </div>
        </section>

        {/* 5. PROOF (REVIEWS + TESTIMONIALS) */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <TestimonialCards />
          </div>
        </section>

        {/* 6. PRICING */}
        <PricingSection variant="full" showSchema={true} />

        {/* 8. HELPFUL GUIDES (MOVED LOW) */}
        <section className="py-16 md:py-20 bg-accent/5">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">Helpful Pricing & Planning Guides</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <NavLink to="/lawn-care-marketing/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Marketing Guide</h3>
                  <p className="text-sm text-muted-foreground">Complete marketing strategy for lawn care companies.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-design-pricing/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design Pricing</h3>
                  <p className="text-sm text-muted-foreground">Understand monthly costs, what's included, and how pricing works.</p>
                </NavLink>
                <NavLink to="/landscaping-website-design-pricing/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design Pricing</h3>
                  <p className="text-sm text-muted-foreground">Pricing clarity for landscapers and hardscapers.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-redesign/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Redesign</h3>
                  <p className="text-sm text-muted-foreground">When to redesign your site and what to expect.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-examples/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Examples</h3>
                  <p className="text-sm text-muted-foreground">See section layouts that convert visitors to leads.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-cost/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Cost</h3>
                  <p className="text-sm text-muted-foreground">Compare pricing options for lawn care websites.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* 9. FINAL CTA + SCHEDULER */}
        <LeadFormSection 
          title="Ready to Get Started?"
          subtitle="Book a 15-minute fit call. We'll confirm your rate, answer questions, and get started on your niche-focused website."
        />
      </div>
    </>
  );
};

export default Websites;
